import React from 'react';
import { Award, Star, Trophy, Users, Sparkles, Film, Music, Tv, Mail, Phone } from 'lucide-react';

const AboutPage = () => {
  // Harish's credentials
  const harishAwards = [
    "Femina Miss India 2023",
    "Miss Diva (2018-2021)",
    "Zee Gold Awards (2011-15)",
    "Indian Telly Awards 2014",
    "34th National Games Ceremony",
    "Doha Opening Ceremony",
    "Sa Re Ga Ma Pa Finale 2013"
  ];

  const harishFilmWork = [
    { movie: "Force 2", role: "Choreography" },
    { movie: "Commando 2", role: "Choreography" },
    { movie: "Sanam Teri Kasam", role: "Choreography" },
    { movie: "Dabangg 3", role: "Choreography" },
    { movie: "Radhe", role: "Choreography" },
    { movie: "Namastey England", role: "Montages" }
  ];

  const expertise = [
    { icon: Sparkles, text: "Large-format stage productions" },
    { icon: Users, text: "Managed audiences of 3,000–5,000+ attendees" },
    { icon: Star, text: "20+ performances in a single evening" },
    { icon: Award, text: "Grand stage design, props, costumes & choreography" }
  ];

  return (
    <>
      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              About Us
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto"></div>
          </div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left: Text Content */}
            <div className="space-y-6">
              <p className="body-font text-xl text-gray-300 leading-relaxed">
                We specialize in transforming conferences into <span className="gold-accent font-semibold">memorable experiences</span> through curated entertainment, elegant concepts, and seamless execution.
              </p>
              
              <p className="heading-font text-2xl gold-accent italic font-light">
                "We don't just manage events—we design experiences your audience remembers."
              </p>

              {/* Credentials */}
              <div className="pt-8 space-y-4">
                <div className="flex items-start gap-4">
                  <Award className="gold-accent flex-shrink-0 mt-1" size={24} />
                  <p className="body-font text-gray-300">
                    <span className="font-semibold text-white">15+ years</span> of excellence in choreography and event curation
                  </p>
                </div>
                
                <div className="flex items-start gap-4">
                  <Star className="gold-accent flex-shrink-0 mt-1" size={24} />
                  <p className="body-font text-gray-300">
                    Collaborated with <span className="font-semibold text-white">Bollywood icons</span> including Salman Khan, Madhuri Dixit, Shah Rukh Khan, and Katrina Kaif
                  </p>
                </div>
                
                <div className="flex items-start gap-4">
                  <Trophy className="gold-accent flex-shrink-0 mt-1" size={24} />
                  <p className="body-font text-gray-300">
                    Featured choreographer for <span className="font-semibold text-white">Colors, Star Plus, and Zee TV</span> with extensive work in Bollywood films, advertisements, and stage shows
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Experience Highlights */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card">
                <h3 className="heading-font text-4xl font-bold gold-accent mb-2">15+</h3>
                <p className="body-font text-gray-400">Years Experience</p>
              </div>
              
              <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card">
                <h3 className="heading-font text-4xl font-bold gold-accent mb-2">500+</h3>
                <p className="body-font text-gray-400">Events Curated</p>
              </div>
              
              <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card">
                <h3 className="heading-font text-4xl font-bold gold-accent mb-2">100+</h3>
                <p className="body-font text-gray-400">Corporate Clients</p>
              </div>
              
              <div className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-yellow-600 transition-all service-card">
                <h3 className="heading-font text-4xl font-bold gold-accent mb-2">50+</h3>
                <p className="body-font text-gray-400">Medical Conferences</p>
              </div>
            </div>
          </div>

          {/* Specialization Note */}
          <div className="mt-16 p-8 bg-zinc-800 border border-yellow-600 rounded-sm">
            <p className="body-font text-lg text-gray-300 text-center leading-relaxed">
              <span className="gold-accent font-semibold">Specialized expertise</span> in medical conference entertainment – from complete event organization to creating extraordinary evening experiences that leave lasting impressions.
            </p>
          </div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <section id="founders-note" className="py-24 px-6 bg-zinc-900">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              Founder's Note
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto"></div>
          </div>

          {/* Founder's Message */}
          <div className="grid md:grid-cols-5 gap-12 items-start">
            {/* Founder Photo */}
            <div className="md:col-span-2 flex justify-center">
              <div className="relative">
                <div className="w-72 h-72 rounded-sm overflow-hidden border-4 border-yellow-600">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/behxv29y_IMG-20190911-WA0006.jpg" 
                    alt="Dr. Akanksha Mantri"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-6 text-center">
                  <h3 className="heading-font text-3xl text-white font-bold mb-2">
                    Dr. Akanksha Mantri
                  </h3>
                  <p className="body-font text-lg gold-accent font-semibold">
                    Founder & Director
                  </p>
                </div>
              </div>
            </div>

            {/* Message Content */}
            <div className="md:col-span-3 space-y-6">
              <p className="body-font text-lg text-gray-300 leading-relaxed">
                Elysian Evenings was born from a simple but powerful belief—<br/>
                that an evening is not just about performances or production, but about <span className="text-white font-semibold">how people feel within it.</span>
              </p>

              <p className="body-font text-lg text-gray-300 leading-relaxed">
                As someone deeply connected to the world of live shows and large-scale productions, I've always been drawn to one thing—the moment when <span className="gold-accent font-semibold">an audience truly connects.</span> When a space stops feeling formal, and people begin to feel present, engaged, and part of something meaningful.
              </p>

              <p className="body-font text-lg text-gray-300 leading-relaxed">
                Being a people-centric and emotionally intuitive person, I naturally approach every event with a sense of <span className="text-white font-semibold">personal ownership.</span> I don't see it as a project—I see it as an experience I would create for myself.
              </p>

              <p className="body-font text-lg text-gray-300 leading-relaxed">
                Over the years, I've been told time and again that I have a natural ability to bring people together, to create energy, and to shape moments that stay with them. That realization led me to build something of my own.
              </p>

              <div className="pt-4 pl-6 border-l-4 border-yellow-600">
                <p className="heading-font text-2xl text-white italic leading-relaxed">
                  Elysian Evenings is an extension of that vision—<br/>
                  where every detail is thoughtfully curated, every experience is personalized, and every evening is designed to leave a lasting impression.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section - Only Harish */}
      <section id="leadership" className="py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
              Leadership
            </h2>
            <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
            <p className="body-font text-xl text-gray-300">
              Meet the creative force behind our productions
            </p>
          </div>

          {/* Harish Profile - Centered */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-zinc-900 p-10 rounded-sm border-2 border-yellow-600">
              {/* Profile Photo */}
              <div className="mb-6 flex justify-center">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-yellow-600">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elysian-evenings/artifacts/yhn4sk33_WhatsApp%20Image%202026-03-25%20at%2015.38.36.jpeg" 
                    alt="Harish Chand Bhatt"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="mb-6 text-center">
                <div className="gold-bg inline-block px-4 py-1 rounded-sm mb-4">
                  <span className="body-font text-xs font-bold text-black">CREATIVE PARTNER</span>
                </div>
                <h3 className="heading-font text-4xl text-white font-bold mb-2">
                  Harish Chand Bhatt
                </h3>
                <p className="body-font text-lg gold-accent font-semibold mb-3">
                  Director • Choreographer • Creative Producer
                </p>
                <div className="space-y-1 mb-6">
                  <p className="body-font text-xs text-gray-400 flex items-center justify-center gap-2">
                    <Mail size={14} className="gold-accent" /> harishbhatt21@gmail.com
                  </p>
                  <p className="body-font text-xs text-gray-400 flex items-center justify-center gap-2">
                    <Phone size={14} className="gold-accent" /> 7678465816 / 9920558702
                  </p>
                </div>
              </div>
              <div className="space-y-3 text-gray-300 body-font text-sm">
                <p>✦ 19+ years in high-impact creative productions</p>
                <p>✦ Founder: Shuli Media & Entertainment, Shuli Records</p>
                <p>✦ Femina Miss India 2023 Creative Director</p>
                <p>✦ Miss Diva Show Director (2018-2021)</p>
                <p>✦ National Games & Doha ceremonies</p>
                <p>✦ Zee Gold Awards, Indian Telly Awards</p>
                <p>✦ Nach Baliye, India's Got Talent, Bigg Boss</p>
              </div>
            </div>
          </div>

          {/* Partnership Statement */}
          <div className="mt-16 mb-16 bg-gradient-to-r from-zinc-900 to-black p-10 rounded-sm border border-zinc-700">
            <h3 className="heading-font text-3xl gold-accent font-bold text-center mb-6">
              A Powerful Creative Partnership
            </h3>
            <p className="body-font text-lg text-white text-center leading-relaxed mb-4">
              <span className="gold-accent font-semibold">Elysian Evenings</span> is founded by <span className="font-bold">Dr. Akanksha Mantri</span>, bringing together 15+ years of choreography excellence and Bollywood expertise.
            </p>
            <p className="body-font text-lg text-gray-300 text-center leading-relaxed">
              In strategic partnership with <span className="text-white font-bold">Harish Chand Bhatt</span>, a seasoned director with 19+ years across national ceremonies and award shows, the team delivers end-to-end execution—combining <span className="gold-accent font-semibold">cinematic storytelling with flawless production</span>.
            </p>
          </div>

          {/* Combined Scale & Expertise */}
          <div className="mb-16">
            <h3 className="heading-font text-3xl font-bold text-white mb-8 text-center">
              Combined Scale & Expertise
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {expertise.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-start gap-4 bg-zinc-900 p-6 rounded-sm border border-zinc-800">
                    <IconComponent className="gold-accent flex-shrink-0 mt-1" size={24} />
                    <p className="body-font text-gray-300">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Harish's Major Projects */}
          <div className="mb-16">
            <h3 className="heading-font text-3xl font-bold text-white mb-8 flex items-center gap-3 justify-center">
              <Award className="gold-accent" size={32} />
              Major Productions & Award Shows
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {harishAwards.map((award, index) => (
                <div key={index} className="bg-zinc-900 p-4 rounded-sm border border-zinc-800 text-center hover:border-yellow-600 transition-colors">
                  <p className="body-font text-gray-300 text-sm">{award}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Film & Television Work */}
          <div className="mb-16">
            <h3 className="heading-font text-3xl font-bold text-white mb-8 flex items-center gap-3 justify-center">
              <Film className="gold-accent" size={32} />
              Film & Television Choreography
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {harishFilmWork.slice(0, 6).map((work, index) => (
                <div key={index} className="bg-zinc-900 p-6 rounded-sm border border-zinc-800 hover:border-yellow-600 transition-colors">
                  <h4 className="heading-font text-xl font-bold text-white mb-2">{work.movie}</h4>
                  <p className="body-font text-gray-400 text-sm">{work.role}</p>
                </div>
              ))}
            </div>

            {/* TV Shows */}
            <div className="mt-8 bg-zinc-900 p-8 rounded-sm border border-zinc-800">
              <h4 className="heading-font text-2xl font-bold gold-accent mb-4 flex items-center gap-3 justify-center">
                <Tv size={24} />
                Television Reality Shows
              </h4>
              <div className="grid md:grid-cols-3 gap-3">
                <p className="body-font text-gray-300">• Nach Baliye (Seasons 3 & 4)</p>
                <p className="body-font text-gray-300">• India's Got Talent</p>
                <p className="body-font text-gray-300">• Bigg Boss Season 3</p>
                <p className="body-font text-gray-300">• Boogie Woogie</p>
                <p className="body-font text-gray-300">• Dancing Queen</p>
                <p className="body-font text-gray-300">• Zee Riston Ka Mela</p>
              </div>
            </div>
          </div>

          {/* Brand Collaborations */}
          <div className="mb-16">
            <h3 className="heading-font text-3xl font-bold text-white mb-8 text-center">
              Brand Collaborations
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {["Kalyan Jewellers", "Titan", "Godrej", "Kent RO", "Liva Fashion", "Ponds", "HSBC", "Times Group"].map((brand, idx) => (
                <div key={idx} className="bg-zinc-900 p-6 rounded-sm border border-yellow-600 text-center">
                  <p className="heading-font text-lg gold-accent font-bold">{brand}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What Sets Apart */}
          <div className="bg-gradient-to-r from-zinc-900 to-black p-10 rounded-sm border border-yellow-600">
            <h3 className="heading-font text-3xl font-bold gold-accent mb-6 text-center">
              What Sets Elysian Evenings Apart
            </h3>
            <p className="body-font text-xl text-white text-center mb-8 italic">
              "We don't just execute events—we design experiences at scale."
            </p>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Audience Engagement</p>
              </div>
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Film className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Visual Storytelling</p>
              </div>
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Seamless Execution</p>
              </div>
              <div className="text-center">
                <div className="gold-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-black" size={28} />
                </div>
                <p className="body-font text-white font-semibold">Premium Positioning</p>
              </div>
            </div>
            <p className="body-font text-lg text-gray-300 text-center leading-relaxed mt-8">
              From Bollywood-style award nights to elegant curated evenings, each experience is crafted to leave a lasting impression.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
